import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuth from './useAuth';
import PATH_PAGE from '@/router/path';
import { getAuthToken } from '@/lib/cookies';
import { DEMO_MODE } from '@/constants';
import { getDeepLinkIntent } from '@/lib/deeplink';

interface PublicOnlyRouteProps {
  children: React.ReactNode;
}

const PublicOnlyRoute: React.FC<PublicOnlyRouteProps> = ({ children }) => {
  const location = useLocation();

  if (DEMO_MODE) {
    return <Navigate to="/demo/dashboard/overview" state={{ from: location }} replace />;
  }

  const { session, isLoading, checkCookieAuth } = useAuth();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    async function verifyAuth() {
      // If we already have a session, consider authenticated
      if (session) {
        setIsAuthenticated(true);
        setIsVerifying(false);
        return;
      }

      // Check for auth token in cookies if no session
      const cookieAuthToken = getAuthToken();
      if (cookieAuthToken) {
        // Attempt to verify the cookie token
        const isValid = await checkCookieAuth();
        setIsAuthenticated(isValid);
      } else {
        setIsAuthenticated(false);
      }
      
      setIsVerifying(false);
    }

    // Only try cookie auth if Supabase auth is done loading
    if (!isLoading) {
      verifyAuth();
    }
  }, [session, isLoading, checkCookieAuth]);

  if (isLoading || isVerifying) {
    return <div />; // Loading state
  }

  if (isAuthenticated) {
    // Check for pending VS Code auth flow
    const vscodeRedirectUri = sessionStorage.getItem('vscode_redirect_uri');
    if (vscodeRedirectUri) {
      // Clear stored params
      sessionStorage.removeItem('vscode_redirect_uri');
      sessionStorage.removeItem('vscode_client_type');
      // Redirect to VS Code auth page to complete the flow
      return <Navigate to={`/auth/vscode?redirect_uri=${encodeURIComponent(vscodeRedirectUri)}`} replace />;
    }

    // Check for pending deep-link intent
    const deepLinkIntent = getDeepLinkIntent();
    if (deepLinkIntent && deepLinkIntent.action === 'gpu-instance') {
      // Redirect to workspaces page which will resolve workspace and redirect to target
      return <Navigate to={PATH_PAGE.root} state={{ from: location, deepLinkIntent }} replace />;
    }

    // Redirect to home if user is already logged in
    return <Navigate to={PATH_PAGE.root} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default PublicOnlyRoute;
